import React, {
  KeyboardEventHandler,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { useDentist } from '@/contexts/DentistContext';
import { useRequests } from '@/contexts/RequestsContext';
import { Request, rescheduleRequest, updateRequest } from '@/data/Requests';
import { useInputFocus, useToast } from '@/hooks';
import { REQUEST_STATUS_OPTIONS } from '@/utils/constants';
import { parseDate } from '@/utils/helpers/parseDate';
import { parseTime } from '@/utils/helpers/parseTime';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  addMinutes,
  format,
  getDay,
  isAfter,
  isBefore,
  isEqual,
  isSameDay,
  startOfDay
} from 'date-fns';

interface RescheduleContextProps {
  date: string | null;
  setDate: React.Dispatch<React.SetStateAction<string | null>>;
  startTime: string | null;
  setStartTime: React.Dispatch<React.SetStateAction<string | null>>;
  endTime: string | null;
  setEndTime: React.Dispatch<React.SetStateAction<string | null>>;
  reason: string;
  setReason: React.Dispatch<React.SetStateAction<string>>;
  errors: any;
  setErrors: React.Dispatch<React.SetStateAction<any>>;
  loading: boolean;
  onInput: (field: string, value: any) => void;
  submit: () => Promise<void>;
  reset: () => void;
  onKeyDown: KeyboardEventHandler<HTMLIonInputElement>;
  conflictingRequest: Request | null;
  inputRef: React.RefObject<HTMLIonInputElement>;
  timeOptions: string[];
}

const RescheduleContext = createContext<RescheduleContextProps | undefined>(
  undefined
);

interface RescheduleProviderProps {
  isOpen?: boolean;
  onClose?: () => void;
  setSelectedDate?: React.Dispatch<React.SetStateAction<Date>>;
  requestToReschedule?: Request | null;
  children: React.ReactNode;
}

export const RescheduleProvider: React.FC<RescheduleProviderProps> = ({
  onClose,
  setSelectedDate,
  requestToReschedule,
  children
}) => {
  const { selectedDentist: dentist, dentistHours } = useDentist();
  const { requests, setRequests, onUpdateStatus, setFocusedRequestId } =
    useRequests();
  const displayToast = useToast();
  const queryClient = useQueryClient();

  const [date, setDate] = useState<string | null>(null);
  const [startTime, setStartTime] = useState<string | null>(null);
  const [endTime, setEndTime] = useState<string | null>(null);
  const [reason, setReason] = useState<string>('');
  const [errors, setErrors] = useState<Record<string, boolean>>({
    date: false,
    startTime: false,
    endTime: false
  });
  const [conflictingRequest, setConflictingRequest] = useState<Request | null>(
    null
  );

  const inputRef = useRef<HTMLIonInputElement>(null);

  useInputFocus(inputRef);

  const updateRequestMutation = useMutation({
    mutationFn: async ({
      requestId,
      request
    }: {
      requestId: string;
      request: Request;
    }) => {
      const updatedRequest = await queryClient.fetchQuery({
        queryKey: ['updateRequest', requestId],
        queryFn: () =>
          updateRequest({
            requestId,
            request
          })
      });
      if (!updatedRequest) {
        throw new Error('Failed to update request');
      }
      return updatedRequest;
    }
  });

  const rescheduleRequestMutation = useMutation({
    mutationFn: async ({
      requestId,
      reason
    }: {
      requestId: string;
      reason: string | null;
    }) => {
      await rescheduleRequest(requestId, reason);
    },
    onSuccess: () => {
      displayToast({
        message:
          'Appointment rescheduled. A notification has been sent to your patient.',
        duration: 5000,
        position: 'bottom',
        positionAnchor: 'tab-bar'
      });
      reset();
      if (onClose) {
        onClose();
      }
      if (setSelectedDate && date) {
        setSelectedDate(startOfDay(parseDate(date)));
      }
      if (requestToReschedule?.id) {
        setFocusedRequestId(requestToReschedule.id);
      }
    },
    onError: () => {
      displayToast({
        message:
          'Failed to reschedule appointment. Please check your internet connection and try again.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'reschedule-button'
      });
    }
  });

  const getTimeOptions = (start: string, end: string) => {
    const startTime = parseTime(start);
    const endTime = parseTime(end);
    const calculatedTimeOptions = [];

    let currentTime = startTime;
    while (isBefore(currentTime, endTime)) {
      calculatedTimeOptions.push(format(currentTime, 'HH:mm'));
      currentTime = addMinutes(currentTime, 30);
    }

    return calculatedTimeOptions;
  };

  const timeOptions = useMemo(() => {
    if (dentistHours.length > 0) {
      const dayOfWeek = getDay(parseDate(date));
      const hoursForDay = dentistHours.find((hours) => hours.day === dayOfWeek);
      if (hoursForDay && hoursForDay.start_time && hoursForDay.end_time) {
        return getTimeOptions(hoursForDay.start_time, hoursForDay.end_time);
      }
    }
    return Array.from({ length: 48 }, (_, i) => {
      const hours = String(Math.floor((i + 8) / 2) % 24).padStart(2, '0');
      const minutes = (i + 12) % 2 === 0 ? '00' : '30';
      return `${hours}:${minutes}`;
    });
  }, [dentistHours, date]);

  const onInput = (field: string, value: any) => {
    switch (field) {
      case 'date':
        setDate(value);
        break;
      case 'startTime':
        setStartTime(value);
        break;
      case 'endTime':
        setEndTime(value);
        break;
      case 'reason':
        setReason(value);
        break;
      default:
        break;
    }
    setErrors((prevErrors) => ({ ...prevErrors, [field]: false }));
  };

  const reset = () => {
    setDate(null);
    setStartTime(null);
    setEndTime(null);
    setReason('');
    setErrors({
      date: false,
      startTime: false,
      endTime: false
    });
  };

  const submit = useCallback(async () => {
    const newErrors = {
      date: !date,
      startTime: !startTime,
      endTime: !endTime
    };

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error)) {
      displayToast({
        message: 'Please fill in all required fields.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'reschedule-button'
      });
      return;
    }

    if (!dentist?.id) {
      setErrors((prevErrors) => ({ ...prevErrors, dentist: true }));
      displayToast({
        message: 'Please select a dentist.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'reschedule-button'
      });
      return;
    }

    if (!requestToReschedule?.id) {
      displayToast({
        message: 'No appointment selected for rescheduling.',
        duration: 3000,
        position: 'bottom',
        positionAnchor: 'reschedule-button'
      });
      return;
    }

    const updatedRequest = await updateRequestMutation.mutateAsync({
      requestId: requestToReschedule.id,
      request: {
        date,
        start_time: startTime,
        end_time: endTime,
        purpose: requestToReschedule.purpose,
        status: requestToReschedule.status,
        dentist_id: requestToReschedule.dentist_id,
        patient_id: requestToReschedule.patient_id
      }
    });

    if (updatedRequest) {
      setRequests(
        requests.map((request) =>
          request.id === updatedRequest.id
            ? { ...request, ...updatedRequest }
            : request
        )
      );

      await rescheduleRequestMutation.mutateAsync({
        requestId: requestToReschedule.id,
        reason: reason.trim() === '' ? null : reason.trim()
      });

      onUpdateStatus(requestToReschedule, REQUEST_STATUS_OPTIONS.RESCHEDULED);
    }
  }, [
    date,
    startTime,
    endTime,
    reason,
    dentist?.id,
    requestToReschedule,
    requests,
    setRequests,
    updateRequestMutation,
    rescheduleRequestMutation,
    onUpdateStatus
  ]);

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLIonInputElement>) => {
      if (event.key === 'Enter') {
        submit();
      }
    },
    [submit]
  );

  useEffect(() => {
    return () => {
      setDate(null);
      setStartTime(null);
      setEndTime(null);
      setReason('');
    };
  }, []);

  useEffect(() => {
    const checkConflictingRequest = () => {
      if (!date || !startTime || !endTime) return;

      const slotStartTime = parseDate(`${date}T${startTime}`);
      const slotEndTime = parseDate(`${date}T${endTime}`);

      const conflict = requests
        .filter(
          (request) =>
            request.id !== requestToReschedule?.id &&
            isSameDay(parseDate(date), parseDate(request.date)) &&
            request.status === REQUEST_STATUS_OPTIONS.APPROVED
        )
        .find((request) => {
          const requestStartTime = parseDate(
            `${request.date}T${request.start_time}`
          );
          const requestEndTime = parseDate(
            `${request.date}T${request.end_time}`
          );

          return (
            ((isAfter(slotStartTime, requestStartTime) ||
              isEqual(slotStartTime, requestStartTime)) &&
              isBefore(slotStartTime, requestEndTime)) ||
            (isAfter(slotEndTime, requestStartTime) &&
              (isBefore(slotEndTime, requestEndTime) ||
                isEqual(slotEndTime, requestEndTime))) ||
            ((isAfter(requestStartTime, slotStartTime) ||
              isEqual(requestStartTime, slotStartTime)) &&
              isBefore(requestStartTime, slotEndTime)) ||
            (isAfter(requestEndTime, slotStartTime) &&
              (isBefore(requestEndTime, slotEndTime) ||
                isEqual(requestEndTime, slotEndTime)))
          );
        });

      setConflictingRequest(conflict ?? null);
    };

    checkConflictingRequest();
  }, [date, startTime, endTime, requests, requestToReschedule]);

  const loading =
    updateRequestMutation.isPending || rescheduleRequestMutation.isPending;

  return (
    <RescheduleContext.Provider
      value={{
        date,
        setDate,
        startTime,
        setStartTime,
        endTime,
        setEndTime,
        reason,
        setReason,
        errors,
        setErrors,
        loading,
        onInput,
        submit,
        reset,
        onKeyDown,
        conflictingRequest,
        inputRef,
        timeOptions
      }}
    >
      {children}
    </RescheduleContext.Provider>
  );
};

export const useReschedule = () => {
  const context = useContext(RescheduleContext);
  if (context === undefined) {
    throw new Error('useReschedule must be used within a RescheduleProvider');
  }
  return context;
};
